/**
 * SoundGuys' `nc-deals-large`: the shared card (Android Authority's A/B pair, `NCDealsLarge` /
 * `NCDealsLargeGrid` by `abTest`, never edited for this) with, since 2026-10-21, the product's
 * strip as its footer — the in-depth card's reading on every surface (Guillaume: "every
 * `nc-deals-large` card on SoundGuys gets the same footer treatment as `nc-deals-large-alt`").
 * The strip is the block the mapper seats on the card (`ownerStrip`, `GuidePick` with `inCard`):
 * score chips, Pin to compare · Measurements · Owners, the pick's reviews `hidden` for crawlers.
 * A card without one — a post whose associated products DealHunt did not resolve, an AA-style
 * page — renders the shared card alone, byte-identical to before.
 *
 * On a review post whose product has published owner reviews the block also carries
 * `reviewOwners`; the card is then the review's one link to its product page (Constraint
 * a4ba5f91): the mapper points its name at the PDP and the strip's Owners block at the owners
 * anchor, and this component keeps the owner card's two events — `review_page_card_click` for
 * the name (location `title`) and `review_page_buy_click` for a store button — from the card's
 * own clicks (one listener on the card's root, the shared card's markup untouched; the shared
 * card still pushes its legacy `widget-interaction`). The owner card this replaces
 * (`ReviewOwnerCard`, design 3a, 2026-09-24 → 2026-10-21) sent the same two.
 */
import { useEffect, useRef } from 'react'
import NCDealsLarge from 'shared-components/nc/NCDealsLarge'
import NCDealsLargeGrid from 'shared-components/nc/NCDealsLargeGrid'
import type { NCComponentInterface } from 'shared-definitions/types'
import GuidePick from 'components/owner-notes/guide/GuidePick'
import { stripRenders } from 'components/owner-notes/guide/strip-rules'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { reviewOwnerScope } from 'lib/owner-notes/analytics'
import type { DSNCDealsLargeSG } from 'types.sg.ds'

/** The card's store buttons, as the shared cards mark them. */
const BUY_SELECTOR = 'a[data-sel="large-deal-button"]'

const NCDealsLargeSG: React.VFC<NCComponentInterface<DSNCDealsLargeSG>> = ({ data }) => {
  const { ownerStrip, reviewOwners, abTest } = data
  const { track } = useCatalogEvent()
  const ref = useRef<HTMLDivElement>(null)
  const pdpHref = reviewOwners?.sg_href ?? null

  // The review's card events, from the card's clicks: the shared card renders the name as
  // `pLink` (the PDP here) and the store buttons with `data-sel`, so one delegated listener
  // names both without touching its markup. The strip's own links report through `GuidePick`.
  useEffect(() => {
    const root = ref.current
    if (!root || !reviewOwners || !pdpHref) {
      return undefined
    }
    const scope = reviewOwnerScope(reviewOwners, 'review_card')
    const onClick = (event: MouseEvent): void => {
      const anchor = (event.target as Element | null)?.closest?.('a')
      if (!anchor || !root.contains(anchor)) {
        return
      }
      if (anchor.matches(BUY_SELECTOR)) {
        track('review_page_buy_click', {
          ...scope,
          location: 'card',
          label: anchor.textContent?.trim() ?? '',
          destination_url: anchor.getAttribute('href') ?? '',
        })
        return
      }
      if (anchor.getAttribute('href') === pdpHref) {
        track('review_page_card_click', { ...scope, location: 'title', destination_url: pdpHref })
      }
    }
    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [reviewOwners, pdpHref, track])

  // The gate before the slot (strip-rules.ts): a pick with no lab curves and no owner review
  // gets no footer at all — the shared card renders as it would without one.
  const footerSlot =
    ownerStrip && stripRenders(ownerStrip.pick.context) ? (
      <GuidePick data={ownerStrip} />
    ) : undefined
  const Card = abTest === 'grid' ? NCDealsLargeGrid : NCDealsLarge
  return <Card ref={ref} data={data} footerSlot={footerSlot} />
}

export default NCDealsLargeSG
